import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { API } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];
const EMAIL_RE = /^\S+@\S+\.\S+$/;

export default function MakingSpace() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("That email doesn't look quite right — try again.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email: email.trim() });
      toast.success("You're on the list. Welcome to the circle.");
      setEmail("");
    } catch {
      toast.error("Something went wrong on our end — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="making-space" data-testid="making-space-section" className="relative overflow-hidden bg-[#F2EFE9] py-28 md:py-40">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 md:px-12 lg:grid-cols-[1.2fr_1fr] lg:gap-24">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-5 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8C382A] md:text-xs"
          >
            Coming September 2026 — A 12-Week Series
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-5xl font-medium leading-[0.98] tracking-tight text-[#131110] md:text-7xl"
          >
            Making <span className="italic text-[#8C382A]">Space</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#131110]/80 md:text-lg"
          >
            Twelve weeks of clearing, claiming, and keeping room for your spirit. Part teaching, part practice,
            part kitchen-table conversation — Making Space is for anyone ready to set down what isn't theirs and
            tend what is. Seats will be limited, and the list hears first.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
          className="self-center"
        >
          <p className="font-display text-2xl italic text-[#131110] md:text-3xl">Leave your email. We'll hold a seat.</p>
          <form data-testid="email-signup-form" onSubmit={submit} className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-end">
            <label className="flex-1">
              <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-[#131110]/60">Email address</span>
              <input
                data-testid="email-signup-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full border-b-2 border-[#131110]/30 bg-transparent pb-3 text-lg text-[#131110] outline-none transition-colors duration-300 placeholder:text-[#131110]/35 focus:border-[#8C382A]"
              />
            </label>
            <button
              data-testid="email-signup-submit-button"
              type="submit"
              disabled={loading}
              className="group flex items-center justify-center gap-3 bg-[#131110] px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-[#F2EFE9] transition-colors duration-300 hover:bg-[#8C382A] disabled:opacity-60"
            >
              {loading ? "Holding…" : "Hold my seat"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </button>
          </form>
          <p className="mt-5 text-xs text-[#131110]/55">One letter when it matters. No noise, no selling your name.</p>
        </motion.div>
      </div>
    </section>
  );
}
